import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';

import type { AllowedCourseItem, SingleCertificateMetadata } from '../../types';
import { certificatesApi } from '../../api';
import { AppDataContext } from '../../context';

import './index.scss';
import messages from './messages';

import whyLeadImg from '../../img/certificate/william-bout-7cdFZmLlWOM-unsplash 1.png';
import leadingSelfImg from '../../img/certificate/neom-xhMz5xIbhRg-unsplash 1.png';
import LeadingTogetherImg from '../../img/certificate/getty-images-u7xBPiZH-5I-unsplash 1.png';
import LeadingChangeImg from '../../img/certificate/oskars-sylwan-TaDYbj5q1Gc-unsplash 1.png';
import goldBadge from '../../img/certificate/certSVG/Gold-Badge.svg';
import graphicImg from '../../img/certificate/certSVG/Graphic.svg';
import graphicBlueImg from '../../img/certificate/certSVG/Graphic-blue.svg';
import leadingChangeImg from '../../img/certificate/certSVG/leading-change.svg';
import leadingForwardImg from '../../img/certificate/certSVG/leading-forward.svg';
import leadingOthersImg from '../../img/certificate/certSVG/leading-others.svg';
import leadingSelfPathImg from '../../img/certificate/certSVG/leading-self.svg';
import legatumBlueLogoImg from '../../img/certificate/certSVG/legatum-blue-logo.svg';
import logoImg from '../../img/certificate/certSVG/Logo.svg';
import whyLeadPathImg from '../../img/certificate/certSVG/why-lead.svg';
import certificateImg from '../../img/certificate/c-logo/certificate.png';
import oxfordImg from '../../img/certificate/c-logo/oxford.png';
import harvardTreeImg from '../../img/certificate/c-logo/harvard_tree.png';

const Certificate: React.FC = () => {
  const [credentialData, setCredentialData] = useState<SingleCertificateMetadata>({} as SingleCertificateMetadata);
  const [credentialDataLoaded, setCredentialDataLoaded] = useState<boolean>(false);
  // TODO: Currently error collects their state but never used
  // eslint-disable-next-line
  const [error, setError] = useState<string | null>(null);

  const intl = useIntl();
  const { credentialID = '' } = useParams();
  const { allowedCourses } = useContext(AppDataContext);
  const {
    certificate_display_name: displayName = credentialData?.course?.name,
    certificate_image_type: imageType = '',
  } = allowedCourses.find(({ course_id: id }: AllowedCourseItem) => (
    id === credentialData?.course?.course_id
  )) ?? {};

  useEffect(() => {
    setCredentialDataLoaded(false);
    setError(null);

    if (!credentialID || !allowedCourses.length) {
      setError(intl.formatMessage(messages.uuidIsEmpty));
      setCredentialDataLoaded(true);
      return;
    }

    const getCertificateMetadataSuccessCB = (res: SingleCertificateMetadata) => {
      setCredentialData(res);
      setCredentialDataLoaded(true);
    };

    const getCertificateMetadataErrorCB = (err: { status?: number, message?: string }) => {
      const status = err?.status;
      setError(
        status === 404
          ? intl.formatMessage(messages.certificateNotFound)
          : intl.formatMessage(messages.certificateLoadError),
      );
      setCredentialDataLoaded(true);
    };

    certificatesApi.getCertificateMetadata(
      credentialID,
      getCertificateMetadataSuccessCB,
      getCertificateMetadataErrorCB,
    );
  }, [credentialID, allowedCourses]);

  useEffect(() => {
    if (credentialDataLoaded && Object.keys(credentialData).length > 0) {
      const certificateContainer = document.getElementById('student-certificate');
      if (certificateContainer) {
        certificateContainer.style.display = 'block';
      }
    }
  }, [credentialData, credentialDataLoaded]);

  const setBGImage = () => {
    const elements = {
      certificateContainer: document.querySelector('.certificate-container') as HTMLElement,
      bodyOverlay: document.querySelector('.body-overlay') as HTMLElement,
      certWrapper: document.querySelector('.cert-wrapper') as HTMLElement,
      body: document.body,
    };

    const backgroundImages: { [key: string]: string } = {
      why_lead: whyLeadImg,
      leading_self: leadingSelfImg,
      leading_others: LeadingTogetherImg,
      leading_change: LeadingChangeImg,
    };

    if (elements.certificateContainer) {
      if (backgroundImages[imageType]) {
        elements.certificateContainer.style.backgroundImage = `url("${backgroundImages[imageType]}")`;
        if (elements.bodyOverlay && ['leading_others', 'leading_change'].includes(imageType)) {
          elements.bodyOverlay.style.display = 'block';
        }
      } else if (imageType === 'leading_forward') {
        elements.certificateContainer.style.backgroundColor = '#fff';
        if (elements.certWrapper) { elements.certWrapper.style.backgroundColor = '#fff'; }
        if (elements.bodyOverlay) { elements.bodyOverlay.style.display = 'block'; }
      }
    }
  };

  const setJourneySVG = () => {
    const svgPaths: { [key: string]: string } = {
      why_lead: whyLeadPathImg,
      leading_self: leadingSelfPathImg,
      leading_others: leadingOthersImg,
      leading_change: leadingChangeImg,
      leading_forward: leadingForwardImg,
    };

    const svgPath = svgPaths[imageType];
    if (svgPath) {
      fetch(svgPath)
        .then(response => response.text())
        .then(data => {
          const parser = new DOMParser();
          const svgDocument = parser.parseFromString(data, 'image/svg+xml');
          const svgRootElement = svgDocument.querySelector('svg');
          const jsvgElement = document.querySelector('.j-svg');
          if (svgRootElement && jsvgElement) {
            jsvgElement.innerHTML = '';
            jsvgElement.appendChild(svgRootElement);
          }
        })
        // eslint-disable-next-line
        .catch(error => logError('Error loading SVG:', error));
    }
  };

  const setCertificateData = (credData: any) => {
    const elements = {
      logoH: document.querySelector('.logo-h') as HTMLImageElement,
      certDescription: document.querySelector('.cert-description') as HTMLElement,
      certWrapper: document.querySelector('.cert-wrapper') as HTMLElement,
      svgImgOverlay: document.querySelector('.svg-img-overlay') as HTMLElement,
      bodyOverlay: document.querySelector('.body-overlay') as HTMLElement,
      body: document.querySelector('body') as HTMLElement,
      certificateContainer: document.querySelector('.certificate-container') as HTMLElement,
      svgImgOverlayBlue: document.querySelector('.svg-img-overlay-blue') as HTMLElement,
      goldBadge: document.querySelector('.gold-badge') as HTMLElement,
    };

    if (imageType === 'leading_forward') {
      if (elements.logoH) { elements.logoH.src = legatumBlueLogoImg; }
      if (elements.certDescription) { elements.certDescription.style.color = '#0A3887'; }
      if (elements.certWrapper) { elements.certWrapper.style.backgroundColor = '#fff'; }
      if (elements.svgImgOverlay) { elements.svgImgOverlay.style.display = 'none'; }
      if (elements.bodyOverlay) { elements.bodyOverlay.classList.add('xs-white-color'); }
      if (elements.body) { elements.body.classList.add('xs-white-color'); }
      if (elements.certWrapper) { elements.certWrapper.classList.add('leading-forward'); }
      if (elements.certificateContainer) { elements.certificateContainer.classList.add('leading-forward'); }
    } else {
      if (elements.logoH) { elements.logoH.src = logoImg; }
      if (elements.svgImgOverlay) { elements.svgImgOverlay.setAttribute('src', graphicImg); }
      if (elements.svgImgOverlayBlue) { elements.svgImgOverlayBlue.style.display = 'none'; }
      if (elements.goldBadge) { elements.goldBadge.style.display = 'none'; }
    }

    const nameElement = document.querySelector<HTMLElement>('.name');
    if (nameElement) {
      nameElement.innerHTML = credData?.recipient?.name;
    }

    const courseNameElement = document.querySelector<HTMLElement>('.course-name');
    if (courseNameElement) {
      courseNameElement.innerHTML = displayName;
    }
  };

  const PrintCertificate = (): void => {
    window.print();
  };

  useEffect(() => {
    if (credentialDataLoaded && credentialData && typeof credentialData === 'object' && 'course' in credentialData) {
      try {
        setBGImage();
        setJourneySVG();
        setCertificateData(credentialData);
      } catch (e) {
        logError(e);
      }
    }
  }, [credentialDataLoaded, credentialData]);

  return (
    credentialDataLoaded ? (
      <>
        <div className="certificate-container" id="student-certificate" style={{ display: 'none' }}>
          <img src={graphicImg} className="svg-img-overlay" alt="" />
          <img src={graphicBlueImg} className="svg-img-overlay-blue" alt="" />
          <img src={goldBadge} className="gold-badge" alt="" />
          <button type="button" className="download-btn-3 download-cert" onClick={PrintCertificate}>
            <div className="link-block-9 w-inline-block">
              <div className="link-block soc lnkd">
                <i className="fas fa-download" aria-hidden="true" />
              </div>
              <div className="button social_btn lnk">
                {intl.formatMessage(messages.downloadButton)}
              </div>
            </div>
          </button>
          <div className="cert-wrapper">
            <img className="logo-h" alt="" />
            <div>
              <h1 className="cert-description">
                {intl.formatMessage(messages.certificateOfAchievement)}
              </h1>
            </div>
            <div className="cert-details">
              <div>{intl.formatMessage(messages.presentedTo)}</div>
              <div className="user_full name" />
              <div>{intl.formatMessage(messages.completionOfModule)}</div>
              <div className="module_name course-name" />
              <div>{intl.formatMessage(messages.courseJourney)}</div>
              <div className="j-svg" />
              <div className="l-w-c">
                {intl.formatMessage(messages.leadingWithCharacterDesc)}
              </div>
              <div className="website-text">
                {intl.formatMessage(messages.website)}
              </div>
              <div className="c-logos">
                <img src={certificateImg} alt="legatum logo" className="c-logo-image-1" />
                <img src={oxfordImg} alt="oxford logo" className="c-logo-image-2" />
                <img src={harvardTreeImg} alt="harvard logo" className="c-logo-image-1" />
              </div>
            </div>
          </div>
        </div>
        <div className="body-overlay" />
      </>
    ) : null
  );
};

export default Certificate;
